import { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, Clock, Flag, FolderClosed, User } from 'lucide-react';
import { Sheet } from '../../ui/Sheet.tsx';
import { useToast } from '../../ui/Toast.tsx';
import { Avatar, Segmented, wsColor } from '../../ui/primitives.tsx';
import { useCreateContent, useCreateTask, useEventMutation, useMembers, useWorkspaces } from '../../api/queries.ts';
import { useOrgCtx, type QuickAddDefaults } from '../../app/org-context.tsx';
import { parseQuickAdd } from '../../lib/nlp.ts';
import { addDays, formatDue } from '../../lib/dates.ts';
import { ApiError } from '../../api/client.ts';

const TOKEN_ICON = { date: Calendar, time: Clock, assignee: User, priority: Flag, workspace: FolderClosed } as const;
type Kind = 'task' | 'content' | 'event';
const COPY: Record<Kind, { placeholder: string; hint: React.ReactNode; dateLabel: string }> = {
  task: { placeholder: 'Cosa c’è da fare?', dateLabel: 'Scadenza', hint: null },
  content: { placeholder: 'Titolo del contenuto', dateLabel: 'Uscita', hint: <>Prova <code>venerdì alle 18</code> <code>#spazio</code></> },
  event: { placeholder: 'Titolo dell’evento', dateLabel: 'Quando', hint: <>Prova <code>domani alle 10</code>. Senza ora l’evento dura tutto il giorno.</> },
};

/** One sheet for everything created from ＋ (TikTok-style): task, PED content or calendar event. */
export function QuickAddSheet({ open, defaults, onClose }: { open: boolean; defaults: QuickAddDefaults; onClose: () => void }) {
  const { orgId, today, openTask, openContent, detail } = useOrgCtx();
  const workspaces = useWorkspaces(orgId);
  const members = useMembers(orgId, detail?.role !== 'guest');
  const createTask = useCreateTask(orgId);
  const createContent = useCreateContent(orgId);
  const createEvent = useEventMutation(orgId);
  const toast = useToast();
  const [kind, setKind] = useState<Kind>(defaults.kind ?? 'task');
  const [text, setText] = useState('');
  const [workspaceId, setWorkspaceId] = useState<string | undefined>(defaults.workspaceId);
  const [dueDate, setDueDate] = useState<string | null>(defaults.dueDate ?? null);
  const [pickedAssignees, setPickedAssignees] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  }, [text, open]);

  useEffect(() => {
    if (open) {
      setText('');
      setError(null);
      setKind(defaults.kind ?? 'task');
      setWorkspaceId(defaults.workspaceId);
      setDueDate(defaults.dueDate ?? (defaults.kind === 'event' ? today : null));
      setPickedAssignees([]);
    }
  }, [open, defaults.kind, defaults.workspaceId, defaults.dueDate, today]);

  const all = workspaces.data ?? [];
  const choices = useMemo(() => {
    if (kind === 'content') return all.filter((w) => w.pedEnabled && (w.isMember || w.canManage));
    return all.filter((w) => w.isMember || w.canManage);
  }, [all, kind]);
  const parsed = useMemo(
    () => parseQuickAdd(text, {
      today,
      people: kind === 'task' ? members.data?.map((m) => ({ id: m.userId, displayName: m.displayName })) : undefined,
      workspaces: choices.map((w) => ({ id: w.id, key: w.key, name: w.name })),
    }),
    [text, today, members.data, choices, kind],
  );
  const pickedWs = parsed.workspaceId ?? workspaceId;
  const targetWs = kind === 'event' ? pickedWs ?? null : pickedWs && choices.some((w) => w.id === pickedWs) ? pickedWs : choices[0]?.id;
  const effectiveDate = parsed.dueDate ?? dueDate;
  const pending = createTask.isPending || createContent.isPending || createEvent.isPending;
  const fail = (e: Error) => setError(e instanceof ApiError ? e.message : 'Non è stato possibile salvare.');
  const done = (keepOpen: boolean, message: string, open?: () => void) => {
    setText('');
    setPickedAssignees([]);
    if (!keepOpen) onClose();
    toast(message, open ? { label: 'Apri', onClick: open } : undefined);
  };

  const submit = (keepOpen: boolean) => {
    if (!parsed.title) return;
    setError(null);
    if (kind === 'task') {
      if (!targetWs) return;
      const assigneeIds = [...new Set([...parsed.assigneeIds, ...pickedAssignees])];
      createTask.mutate(
        { workspaceId: targetWs, title: parsed.title, dueDate: effectiveDate, dueTime: parsed.dueTime, priority: parsed.priority ?? 0, assigneeIds },
        { onSuccess: (t) => done(keepOpen, `${t.key} creata`, () => openTask(t.id)), onError: fail },
      );
    } else if (kind === 'content') {
      if (!targetWs) return;
      createContent.mutate(
        { workspaceId: targetWs, title: parsed.title, plannedDate: effectiveDate, plannedTime: parsed.dueTime },
        { onSuccess: (c) => done(keepOpen, `Aggiunto al PED di ${c.workspaceName}`, () => openContent(c.id)), onError: fail },
      );
    } else {
      const date = effectiveDate ?? today;
      createEvent.mutate(
        { input: { title: parsed.title, date, startTime: parsed.dueTime, endTime: null, workspaceId: targetWs ?? null, endDate: null, location: null, description: null } },
        { onSuccess: () => done(keepOpen, `Evento aggiunto al calendario · ${formatDue(date, parsed.dueTime, today)}`), onError: fail },
      );
    }
  };

  const noPed = kind === 'content' && workspaces.data && !choices.length;

  return (
    <Sheet open={open} onClose={onClose} title="Crea" variant="center" labelledBy="quick-add-title">
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
        <Segmented label="Cosa vuoi creare" value={kind} onChange={(k) => { setKind(k); setError(null); }}
          options={[{ value: 'task', label: 'Task' }, { value: 'content', label: 'Contenuto' }, { value: 'event', label: 'Evento' }]} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); submit(false); }}>
        <textarea
          ref={inputRef} data-autofocus rows={1} className="quick-input" placeholder={COPY[kind].placeholder} value={text}
          onChange={(e) => setText(e.target.value.replace(/\n/g, ' '))}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(e.metaKey || e.ctrlKey); } }}
          aria-label={COPY[kind].placeholder} autoComplete="off" enterKeyHint="done"
        />
        <div className="token-chips" aria-live="polite">
          {parsed.tokens.filter((t) => kind === 'task' || (t.kind !== 'assignee' && t.kind !== 'priority')).map((t) => {
            const Icon = TOKEN_ICON[t.kind];
            const ws = t.kind === 'workspace' ? choices.find((w) => w.id === parsed.workspaceId) : undefined;
            return (
              <span key={t.kind + t.text} className={`token ${t.kind}`} style={ws ? ({ '--ws-color': wsColor(ws.color) } as React.CSSProperties) : undefined}>
                <Icon size={13} /> {t.kind === 'date' && parsed.dueDate ? formatDue(parsed.dueDate, null, today) : t.label}
              </span>
            );
          })}
        </div>

        {!parsed.dueDate && (
          <div className="quick-row" role="group" aria-label={COPY[kind].dateLabel}>
            {[
              { label: 'Oggi', value: today },
              { label: 'Domani', value: addDays(today, 1) },
              ...(kind === 'event' ? [] : [{ label: kind === 'content' ? 'Da decidere' : 'Nessuna', value: null }]),
            ].map((o) => (
              <button key={o.label} type="button" className="chip" aria-pressed={effectiveDate === o.value} onClick={() => setDueDate(o.value)}>
                {o.value && <Calendar size={14} />} {o.label}
              </button>
            ))}
            {kind !== 'task' && (
              <input type="date" className="chip" aria-label="Scegli una data" value={effectiveDate ?? ''} onChange={(e) => setDueDate(e.target.value || null)} style={{ border: 0 }} />
            )}
          </div>
        )}

        {!parsed.workspaceId && (choices.length > 1 || kind === 'event') && (
          <div className="chips" style={{ marginTop: 12, marginBottom: 0 }} role="group" aria-label="Spazio">
            {kind === 'event' && (
              <button type="button" className="chip" aria-pressed={!targetWs} onClick={() => setWorkspaceId(undefined)}>Tutta l’azienda</button>
            )}
            {choices.map((w) => (
              <button key={w.id} type="button" className="chip" aria-pressed={targetWs === w.id} onClick={() => setWorkspaceId(w.id)}>
                <span className="ws-dot" style={{ '--ws-color': wsColor(w.color) } as React.CSSProperties} />{w.name}
              </button>
            ))}
          </div>
        )}

        {kind === 'task' && (members.data?.length ?? 0) > 1 && (
          <div className="chips" style={{ marginTop: 12, marginBottom: 0 }} role="group" aria-label="Assegna a">
            {members.data!.map((m) => {
              const on = pickedAssignees.includes(m.userId) || parsed.assigneeIds.includes(m.userId);
              return (
                <button
                  key={m.userId}
                  type="button"
                  className="person-chip"
                  aria-pressed={on}
                  onClick={() => setPickedAssignees((ids) => (ids.includes(m.userId) ? ids.filter((id) => id !== m.userId) : [...ids, m.userId]))}
                >
                  <Avatar id={m.userId} name={m.displayName} avatarUrl={m.avatarUrl} size={20} />
                  {m.displayName.split(' ')[0]}
                </button>
              );
            })}
          </div>
        )}

        {error && <div className="form-error" style={{ marginTop: 12 }}>{error}</div>}
        {noPed && <p className="hint" style={{ marginTop: 12 }}>Nessuno spazio ha il calendario editoriale attivo. Aprilo da uno spazio → Calendario.</p>}

        <div className="quick-row" style={{ marginTop: 18, justifyContent: COPY[kind].hint ? 'space-between' : 'flex-end' }}>
          {COPY[kind].hint && <p className="quick-hint" style={{ flex: 1, margin: 0 }}>{COPY[kind].hint}</p>}
          <button type="submit" className="btn btn-primary" disabled={!parsed.title || (kind !== 'event' && !targetWs) || pending}>Aggiungi</button>
        </div>
      </form>
    </Sheet>
  );
}
